/**
 * navigator.geolocation wrapper.
 *
 * Per AGENTS.md §14:
 * - enableHighAccuracy: true forces the GPS chip when available.
 * - timeout: 15s; maximumAge: 0 (no cached fix).
 * - HTTPS is mandatory — Caddy/TLS handles this.
 * - On iOS Safari the call MUST be triggered from a user gesture (button
 *   tap), not page load. Callers wire this into their "Continue" handler.
 *
 * The accuracy_m field drives the report's evidence-grade classification:
 *   < 30 m   → GPS-verified
 *   30–500 m → Wi-Fi / cell verified
 *   > 500 m  → imprecise (flagged in the report)
 */
export type GeoSource = "gps" | "wifi" | "cell" | "ip" | "unknown";

export type GeoFix = {
  lat: number;
  lng: number;
  accuracy_m: number;
  altitude_m: number | null;
  altitude_accuracy_m: number | null;
  source: GeoSource;
  timestamp: number;
};

export type GeoOutcome =
  | { ok: true; fix: GeoFix }
  | { ok: false; code: "denied" | "unavailable" | "timeout" | "unsupported"; message: string };

/** Heuristic source label from accuracy_m — the browser doesn't tell us
 * whether the fix came from GPS, Wi-Fi, or cell, so we infer. */
export function inferSource(accuracy_m: number): GeoSource {
  if (!Number.isFinite(accuracy_m)) return "unknown";
  if (accuracy_m < 30) return "gps";
  if (accuracy_m < 500) return "wifi";
  if (accuracy_m < 5000) return "cell";
  return "ip";
}

function _acquireOnce(timeout: number, maximumAge: number): Promise<GeoOutcome> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({
        ok: false,
        code: "unsupported",
        message: "Geolocation is not available in this browser.",
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy, altitude, altitudeAccuracy } = position.coords;
        resolve({
          ok: true,
          fix: {
            lat: latitude,
            lng: longitude,
            accuracy_m: accuracy,
            altitude_m: altitude,
            altitude_accuracy_m: altitudeAccuracy,
            source: inferSource(accuracy),
            timestamp: position.timestamp,
          },
        });
      },
      (err) => {
        let code: "denied" | "unavailable" | "timeout" = "unavailable";
        if (err.code === err.PERMISSION_DENIED) code = "denied";
        else if (err.code === err.TIMEOUT) code = "timeout";
        resolve({ ok: false, code, message: err.message });
      },
      { enableHighAccuracy: true, timeout, maximumAge },
    );
  });
}

/** POST a fix to the session-geo endpoint using the field-session token
 * (mirrors uploadFieldSnapshot's hand-rolled auth so the shared `api`
 * instance's Bearer interceptor doesn't clobber it). Best-effort: geo is
 * non-essential, so callers swallow failures. */
export async function postFieldSessionGeo(
  sessionId: string,
  fieldSessionToken: string,
  fix: GeoFix,
): Promise<void> {
  const { default: axios } = await import("axios");
  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  await axios.post(
    `${apiBase}/v1/sessions/${sessionId}/geo`,
    {
      lat: fix.lat,
      lng: fix.lng,
      accuracy_m: fix.accuracy_m,
      source: fix.source,
      timestamp: fix.timestamp,
      ...(fix.altitude_m !== null ? { altitude_m: fix.altitude_m } : {}),
      ...(fix.altitude_accuracy_m !== null
        ? { altitude_accuracy_m: fix.altitude_accuracy_m }
        : {}),
    },
    {
      headers: {
        "Content-Type": "application/json",
        "X-Field-Session-Token": fieldSessionToken,
      },
    },
  );
}

/** Acquire a GPS fix, retrying transient failures. A cold mobile GPS can take
 * 10–20s to get a first fix, so we use a generous timeout and retry
 * timeout/unavailable outcomes; a `denied`/`unsupported` result is terminal
 * (retrying won't change it). On retry we accept a slightly cached fix
 * (`maximumAge`) so a fix that landed between attempts still counts. */
export async function acquireGeolocation(
  opts: { retries?: number; timeout?: number; maximumAge?: number } = {},
): Promise<GeoOutcome> {
  const retries = opts.retries ?? 1;
  const timeout = opts.timeout ?? 20_000;
  const baseMaxAge = opts.maximumAge ?? 0;
  let last: GeoOutcome = {
    ok: false,
    code: "unavailable",
    message: "no attempt",
  };
  for (let attempt = 0; attempt <= retries; attempt++) {
    // First attempt wants a fresh fix; later attempts accept a recent cached
    // one (a fix may have arrived while we waited).
    const maxAge = attempt === 0 ? baseMaxAge : Math.max(baseMaxAge, 30_000);
    last = await _acquireOnce(timeout, maxAge);
    if (last.ok) return last;
    if (last.code === "denied" || last.code === "unsupported") return last;
  }
  return last;
}